import { Button, CloseButton, Dialog, DialogBody, DialogContent, DialogFooter, Drawer, Flex, Portal, Text } from '@chakra-ui/react';
import { FormInput, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAddSuperAdmin, useDeleteUser, useGetSingleUser, useUpdateSuperAdmin } from '../../api/hooks/useUserSuperadmin';

type SuperAdminForm = {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
};

export default function SuperadminDrawer({ isSelf, userID }: { isSelf: boolean; userID?: number }) {
	const { t } = useTranslation();
	const { mutate: uUser, isPending: isUpdatingUser } = useUpdateSuperAdmin();
	const { mutate: addUser, isPending: isAddingUser } = useAddSuperAdmin();

	const { closeDrawer } = useDrawer();
	const [isRemoveOpen, setIsRemoveOpen] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const { data: user, isFetching: isFetchingUser } = useGetSingleUser(userID as number, {
		isEnabled: !!userID,
	});

	const methods = useForm<SuperAdminForm>({
		defaultValues: {
			firstName: user?.firstName || '',
			lastName: user?.lastName || '',
			phone: user?.phone || '',
			email: user?.email || '',
		},
	});

	const {
		handleSubmit,
		reset,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (user) {
			reset({
				firstName: user.firstName,
				lastName: user.lastName,
				phone: user.phone,
				email: user.email,
			});
		}
	}, [user, reset]);

	function saveUser(values: SuperAdminForm): void {
		if (userID) {
			updateUser(values);
		} else {
			const payload = {
				email: values.email,
				superAdmin: {
					create: true,
				},
			};

			addUser(payload, {
				onSuccess: async () => {
					toaster.create({
						title: t('general.saved'),
						type: 'success',
					});
					queryClient.refetchQueries({ queryKey: ['users'] });
					queryClient.refetchQueries({ queryKey: ['user'] });

					closeDrawer('superadminDrawer');
				},
			});
		}
	}

	function updateUser(values: SuperAdminForm): void {
		if (!userID) {
			return;
		}
		const payload: UpdateSuperAdminRequest = {
			userID: userID,
		};

		(Object.keys(dirtyFields) as Array<keyof SuperAdminForm>).forEach(key => {
			const value = values[key];

			// Do not allow updating email
			if (value !== undefined && key !== 'email') {
				payload[key] = value;
			}
		});

		uUser(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['users'] });
				queryClient.refetchQueries({ queryKey: ['user'] });

				closeDrawer('superadminDrawer');
			},
		});
	}

	return isFetchingUser ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(saveUser)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
						{userID ? t('general.edit_superadmin') : t('general.add_superadmin')}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						{userID ? (
							<>
								<FormInput label={t('general.first_name')} name="firstName" maxLength={100} />
								<FormInput label={t('general.last_name')} name="lastName" maxLength={100} />
							</>
						) : null}
						<FormInput disabled={userID ? true : false} isRequired label={t('general.email')} name="email" maxLength={100} />

						{userID ? (
							<FormInput
								inputProps={{ style: { width: '100%' } }}
								helperText={t('general.phone_in_international_format')}
								helperTextProps={{ style: { textAlign: 'start' } }}
								type="tel"
								label={t('general.phone')}
								name="phone"
								maxLength={30}
							/>
						) : null}

						{!userID ? <Text>{t('general.user_will_receive_an_email')}</Text> : null}
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					{userID ? (
						<>
							<RemoveUser isSelf={isSelf} userId={userID} isOpen={isRemoveOpen} setIsOpen={setIsRemoveOpen} />
							<Button id="removeUserButton" marginRight={'auto'} onClick={() => setIsRemoveOpen(true)} variant="subtle">
								{t('general.remove_from_account')}
							</Button>
						</>
					) : null}
					<Button onClick={() => closeDrawer('userDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingUser || isAddingUser || !isDirty}>
						{isUpdatingUser ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}

function RemoveUser({ isSelf, userId, isOpen, setIsOpen }: { isSelf: boolean; userId: number; isOpen: boolean; setIsOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const { closeDrawer } = useDrawer();
	const { mutate, isPending: isDeletingUser } = useDeleteUser();
	const queryClient = useQueryClient();

	function removeUser(): void {
		if (isSelf) {
			toaster.create({
				type: 'error',
				title: t('general.i_just_tried_to_do_something_very_silly'),
			});
			return;
		}
		mutate(userId, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.removed'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['users'] });
				closeDrawer('superadminDrawer');
			},
		});
	}

	return (
		<Dialog.Root open={isOpen} onOpenChange={e => setIsOpen(e.open)} placement="center">
			<Dialog.Backdrop />
			<Portal>
				<Dialog.Positioner pointerEvents="none">
					<DialogContent>
						<Dialog.Header>
							<Dialog.Title>{t('general.remove_user')}</Dialog.Title>
						</Dialog.Header>
						<Dialog.CloseTrigger />
						<DialogBody>
							<p>{t('general.remove_user')}?</p>
						</DialogBody>
						<DialogFooter>
							<Dialog.ActionTrigger asChild>
								<Button variant="outline">{t('general.cancel')}</Button>
							</Dialog.ActionTrigger>
							<Button id="confirmRemoveUserButton" disabled={isDeletingUser} variant="subtle" onClick={removeUser}>
								{isDeletingUser ? t('general.please_wait') : t('general.remove')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
}
